import { Link } from 'react-router-dom';
import { Compass, Sparkles, FileCode, FolderOpen, Cloud, Share2 } from 'lucide-react';
import { useAuth } from '../auth/store';

const LandingPage = () => {
  const user = useAuth((s) => s.user);
  return (
    <div className="min-h-full bg-slate-950 text-slate-100">
      <header className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-archer-500 text-white">
            <Compass className="h-5 w-5" />
          </div>
          <span className="text-lg font-semibold">Omniapps Archer</span>
        </div>
        <div>
          {user ? (
            <Link to="/projects" className="btn-primary">
              Open projects
            </Link>
          ) : (
            <Link to="/login" className="btn-primary">
              Sign in
            </Link>
          )}
        </div>
      </header>

      <section className="mx-auto max-w-5xl px-6 py-20 text-center">
        <h1 className="text-4xl font-bold tracking-tight md:text-6xl">
          Architecture diagrams, from <span className="text-archer-400">prompt</span> to{' '}
          <span className="text-archer-400">editable canvas</span>.
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300">
          Archer turns natural-language descriptions into Mermaid and C4 diagrams, then lets you
          take them apart — drag every node, reflow every connector — on an infinite canvas. Keep
          them in the cloud, on your own disk, or right in the browser.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to={user ? '/projects' : '/login'} className="btn-primary text-base">
            <Sparkles className="h-4 w-4" />
            {user ? 'Open projects' : 'Get started'}
          </Link>
          <a href="#features" className="btn-secondary text-base">
            Learn more
          </a>
        </div>
      </section>

      <section id="features" className="mx-auto grid max-w-5xl grid-cols-1 gap-4 px-6 pb-20 md:grid-cols-3">
        <FeatureCard
          icon={<Sparkles className="h-5 w-5 text-archer-400" />}
          title="AI-first"
          text="Describe what you want — C4 context, sequence flow, state machine — Archer generates clean Mermaid and renders it instantly."
        />
        <FeatureCard
          icon={<FileCode className="h-5 w-5 text-archer-400" />}
          title="Editable"
          text="Every node and arrow becomes a native canvas primitive. Drag, resize, restyle. Mermaid stays as the source of truth."
        />
        <FeatureCard
          icon={<FolderOpen className="h-5 w-5 text-archer-400" />}
          title="Your data, your rules"
          text="Store projects in the Archer cloud, a folder you choose on your disk, or in your browser. Switch any time."
        />
        <FeatureCard
          icon={<Cloud className="h-5 w-5 text-archer-400" />}
          title="Omniapps native"
          text="Single sign-on through Omniapps Account. AI features charge credits against your existing balance."
        />
        <FeatureCard
          icon={<Share2 className="h-5 w-5 text-archer-400" />}
          title="Shareable"
          text="Generate invite links with viewer or editor roles. Collaborate in real-time (coming soon)."
        />
        <FeatureCard
          icon={<Compass className="h-5 w-5 text-archer-400" />}
          title="PWA"
          text="Install Archer as a desktop or phone app. Works offline once a project is loaded."
        />
      </section>

      <footer className="border-t border-slate-800 px-6 py-6 text-center text-xs text-slate-500">
        Archer is part of the{' '}
        <a className="underline" href="https://omniapps.net" target="_blank" rel="noreferrer">
          Omniapps
        </a>{' '}
        family. v0.1.
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) => (
  <div className="card text-left">
    <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-archer-500/10 ring-1 ring-archer-500/30">
      {icon}
    </div>
    <h3 className="mb-1 text-sm font-semibold">{title}</h3>
    <p className="text-sm text-slate-400">{text}</p>
  </div>
);

export default LandingPage;
